import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import {
  getGeniusLabs, addIdea, updateIdea, deleteIdea,
  addEdge, deleteEdge, saveNodePositions,
  saveMindmap, getMindmap,
  getOpenAIKey, setOpenAIKey,
  type IdeaNode, type IdeaEdge, type MindmapData, type MindmapBranch,
} from "@/lib/geniusLabsStore";
import {
  Sparkles, Plus, Trash2, Network, Pencil, X, Check,
  Loader2, ZoomIn, ZoomOut, RotateCcw, Link, Unlink,
  Tag, ChevronRight, Upload, FileJson, Settings, Eye, EyeOff,
  BrainCircuit, ChevronDown,
} from "lucide-react";
import CCBSkillHint, { ccbNoteKey, type CCBSavedNote } from "@/components/CCBSkillHint";
import { BookText } from "lucide-react";

const BASE = process.env.REACT_APP_BACKEND_URL;

// ── Force simulation ──────────────────────────────────────────────────────────
const SPRING_K = 0.04;
const SPRING_REST = 180;
const REPULSE_C = 7000;
const CENTER_PULL = 0.005;
const DAMPING = 0.82;
const NODE_R = 38;

interface SimNode extends IdeaNode { vx: number; vy: number; }
function initSim(nodes: IdeaNode[]): SimNode[] {
  return nodes.map((n) => ({ ...n, vx: 0, vy: 0 }));
}

// ── Mindmap layout ────────────────────────────────────────────────────────────
interface MapNode { id: string; label: string; note?: string; x: number; y: number; depth: number; color: string; }
interface MapEdge { from: string; to: string; }

const BRANCH_COLORS = [
  "#6366f1","#0ea5e9","#10b981","#f59e0b",
  "#ef4444","#8b5cf6","#ec4899","#14b8a6","#f97316","#84cc16",
];

function layoutMindmap(data: MindmapData, cx: number, cy: number) {
  const mapNodes: MapNode[] = [];
  const mapEdges: MapEdge[] = [];

  mapNodes.push({ id: "root", label: data.root, x: cx, y: cy, depth: 0, color: "#475569" });

  const branchCount = data.branches.length;
  data.branches.forEach((branch: MindmapBranch, bi: number) => {
    const angle = (bi / branchCount) * 2 * Math.PI - Math.PI / 2;
    const color = BRANCH_COLORS[bi % BRANCH_COLORS.length];
    const r1 = 200;
    const bx = cx + Math.cos(angle) * r1;
    const by = cy + Math.sin(angle) * r1;
    const bid = `b${bi}`;
    mapNodes.push({ id: bid, label: branch.label, x: bx, y: by, depth: 1, color });
    mapEdges.push({ from: "root", to: bid });

    const childCount = branch.children.length;
    if (childCount === 0) return;
    const halfSpread = Math.PI / Math.max(branchCount, 3);
    branch.children.forEach((child, ci: number) => {
      const childAngle = angle - halfSpread + (ci + 0.5) / childCount * halfSpread * 2;
      const r2 = 370;
      const cid = `b${bi}c${ci}`;
      mapNodes.push({
        id: cid, label: child.label, note: child.note,
        x: cx + Math.cos(childAngle) * r2,
        y: cy + Math.sin(childAngle) * r2,
        depth: 2, color,
      });
      mapEdges.push({ from: bid, to: cid });
    });
  });
  return { mapNodes, mapEdges };
}

// ── Chatbot JSON parsers ──────────────────────────────────────────────────────
interface ChatConversation { id: string; title: string; text: string; }

// ChatGPT export format (conversations.json with mapping property)
function parseChatGPTExport(json: any): ChatConversation[] {
  if (!Array.isArray(json)) return [];
  return json.map((conv: any) => {
    const title: string = conv.title || "Untitled";
    const lines: string[] = [];
    if (conv.mapping) {
      const nodes = Object.values(conv.mapping) as any[];
      nodes.sort((a, b) => (a.message?.create_time ?? 0) - (b.message?.create_time ?? 0));
      for (const node of nodes) {
        const msg = node.message;
        if (!msg) continue;
        const role: string = msg.author?.role;
        if (role !== "user" && role !== "assistant") continue;
        const parts: any[] = msg.content?.parts ?? [];
        const text = parts.filter((p: any) => typeof p === "string").join(" ").trim();
        if (!text) continue;
        lines.push(`${role === "user" ? "User" : "Assistant"}: ${text}`);
      }
    }
    return { id: conv.id || title, title, text: lines.join("\n") };
  }).filter((c) => c.text.length > 20);
}

// Open WebUI / generic chatbot export format (items with chat.messages array)
function parseOpenWebUIExport(json: any): ChatConversation[] {
  if (!Array.isArray(json)) return [];
  return json.map((conv: any) => {
    const title: string = conv.title || "Untitled";
    const lines: string[] = [];
    const messages: any[] = conv.chat?.messages ?? [];
    for (const msg of messages) {
      const role: string = msg.role;
      if (role !== "user" && role !== "assistant") continue;
      // Prefer content_list phase="answer" for assistant messages
      let text = "";
      if (Array.isArray(msg.content_list)) {
        const answer = msg.content_list.find((item: any) => item.phase === "answer");
        if (answer) text = (answer.content as string) || "";
      }
      if (!text && typeof msg.content === "string") text = msg.content;
      if (!text) continue;
      lines.push(`${role === "user" ? "User" : "Assistant"}: ${text}`);
    }
    return { id: conv.id || title, title, text: lines.join("\n") };
  }).filter((c) => c.text.length > 20);
}

// ── OpenAI system prompt ──────────────────────────────────────────────────────
const MINDMAP_PROMPT = `You are an expert at distilling conversations into structured knowledge maps.

Given a conversation transcript, extract all key ideas, topics, decisions, and concepts. Organize them into a hierarchical mindmap.

Return ONLY valid JSON with this exact structure:
{
  "root": "main overarching theme of the conversation (4-8 words)",
  "branches": [
    {
      "label": "major theme or category (3-6 words)",
      "children": [
        { "label": "specific idea or concept (2-8 words)", "note": "one sentence elaboration if helpful" }
      ]
    }
  ]
}

Rules:
- Create 4-8 top-level branches covering the main themes
- Each branch should have 3-8 children (specific ideas under that theme)
- Labels should be concise but meaningful
- Capture insights, decisions, plans, problems, and solutions
- Group related ideas together into the same branch
- The root should capture the essence of the whole conversation`;

// ── Props ─────────────────────────────────────────────────────────────────────
interface Props { projectId: string; showSkillLensNotes?: boolean; }
type View = "capture" | "graph" | "mindmap" | "notes" | "settings";

// ── Component ─────────────────────────────────────────────────────────────────
export default function GeniusLabsPanel({ projectId, showSkillLensNotes = true }: Props) {
  const { token } = useAuth();
  const authHeader = token ? { Authorization: `Bearer ${token}` } : {};

  const [view, setView] = useState<View>("capture");
  const [nodes, setNodes] = useState<IdeaNode[]>([]);
  const [edges, setEdges] = useState<IdeaEdge[]>([]);
  const [mindmap, setMindmap] = useState<MindmapData | null>(null);

  // Capture
  const [captureTitle, setCaptureTitle] = useState("");
  const [captureBody, setCaptureBody] = useState("");
  const [captureTags, setCaptureTags] = useState("");
  const [editId, setEditId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState("");
  const [editBody, setEditBody] = useState("");

  // AI connect (Ollama)
  const [aiLoading, setAiLoading] = useState(false);
  const [pendingEdges, setPendingEdges] = useState<{ fromId: string; toId: string; label: string; reason: string }[]>([]);

  // ChatGPT import
  const fileRef = useRef<HTMLInputElement>(null);
  const [conversations, setConversations] = useState<ChatConversation[]>([]);
  const [selectedConvIds, setSelectedConvIds] = useState<Set<string>>(new Set());
  const [importLoading, setImportLoading] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [importedCount, setImportedCount] = useState<number | null>(null);

  // Settings
  const [apiKey, setApiKey] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [keySaved, setKeySaved] = useState(false);

  // Graph sim
  const svgRef = useRef<SVGSVGElement>(null);
  const simNodesRef = useRef<SimNode[]>([]);
  const rafRef = useRef<number | null>(null);
  const [simNodes, setSimNodes] = useState<SimNode[]>([]);
  const [graphEdges, setGraphEdges] = useState<IdeaEdge[]>([]);
  const [dragging, setDragging] = useState<string | null>(null);
  const dragOffset = useRef({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const panStart = useRef<{ mx: number; my: number; px: number; py: number } | null>(null);
  const [selectedNode, setSelectedNode] = useState<string | null>(null);
  const [selectedEdge, setSelectedEdge] = useState<string | null>(null);
  const [linking, setLinking] = useState<string | null>(null);
  const [simRunning, setSimRunning] = useState(true);

  // Mindmap pan/zoom
  const [mapZoom, setMapZoom] = useState(1);
  const [mapPan, setMapPan] = useState({ x: 0, y: 0 });
  const mapPanStart = useRef<{ mx: number; my: number; px: number; py: number } | null>(null);
  const [collapsedBranches, setCollapsedBranches] = useState<Set<string>>(new Set());
  const [hoveredMapNode, setHoveredMapNode] = useState<string | null>(null);

  // ── Load ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    const data = getGeniusLabs(projectId);
    setNodes(data.nodes);
    setEdges(data.edges);
    setMindmap(getMindmap(projectId));
    setApiKey(getOpenAIKey());
  }, [projectId]);

  useEffect(() => {
    const sn = initSim(nodes);
    simNodesRef.current = sn;
    setSimNodes([...sn]);
    setGraphEdges([...edges]);
  }, [nodes, edges]);

  // ── Force sim ─────────────────────────────────────────────────────────────
  const runSim = useCallback(() => {
    if (!simRunning) return;
    const sn = simNodesRef.current;
    if (sn.length < 2) return;
    const cx = 500, cy = 300;
    for (let i = 0; i < sn.length; i++) {
      let fx = (cx - sn[i].x) * CENTER_PULL;
      let fy = (cy - sn[i].y) * CENTER_PULL;
      for (let j = 0; j < sn.length; j++) {
        if (i === j) continue;
        const dx = sn[i].x - sn[j].x, dy = sn[i].y - sn[j].y;
        const dist = Math.sqrt(dx * dx + dy * dy) || 1;
        const f = REPULSE_C / (dist * dist);
        fx += (dx / dist) * f; fy += (dy / dist) * f;
      }
      for (const edge of graphEdges) {
        let other = -1;
        if (edge.fromId === sn[i].id) other = sn.findIndex((n) => n.id === edge.toId);
        else if (edge.toId === sn[i].id) other = sn.findIndex((n) => n.id === edge.fromId);
        if (other === -1) continue;
        const dx = sn[other].x - sn[i].x, dy = sn[other].y - sn[i].y;
        const dist = Math.sqrt(dx * dx + dy * dy) || 1;
        fx += (dx / dist) * SPRING_K * (dist - SPRING_REST);
        fy += (dy / dist) * SPRING_K * (dist - SPRING_REST);
      }
      if (dragging !== sn[i].id) {
        sn[i].vx = (sn[i].vx + fx) * DAMPING;
        sn[i].vy = (sn[i].vy + fy) * DAMPING;
        sn[i].x = Math.max(NODE_R + 10, Math.min(990 - NODE_R, sn[i].x + sn[i].vx));
        sn[i].y = Math.max(NODE_R + 10, Math.min(590 - NODE_R, sn[i].y + sn[i].vy));
      }
    }
    setSimNodes([...sn]);
    rafRef.current = requestAnimationFrame(runSim);
  }, [simRunning, graphEdges, dragging]);

  useEffect(() => {
    if (view !== "graph") { if (rafRef.current) cancelAnimationFrame(rafRef.current); return; }
    rafRef.current = requestAnimationFrame(runSim);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [view, runSim]);

  // ── Capture ───────────────────────────────────────────────────────────────
  const handleAdd = () => {
    const title = captureTitle.trim();
    if (!title) return;
    const tags = captureTags.split(",").map((t) => t.trim()).filter(Boolean);
    const node = addIdea(projectId, title, captureBody, tags, 900, 550);
    setNodes((prev) => [...prev, node]);
    setCaptureTitle(""); setCaptureBody(""); setCaptureTags("");
  };

  const handleDelete = (id: string) => {
    deleteIdea(projectId, id);
    setNodes((prev) => prev.filter((n) => n.id !== id));
    setEdges((prev) => prev.filter((e) => e.fromId !== id && e.toId !== id));
  };

  const handleEditSave = () => {
    if (!editId) return;
    updateIdea(projectId, editId, { title: editTitle, body: editBody });
    setNodes((prev) => prev.map((n) => n.id === editId ? { ...n, title: editTitle, body: editBody } : n));
    setEditId(null);
  };

  // ── Ollama AI connect ─────────────────────────────────────────────────────
  const handleAiConnect = async () => {
    if (nodes.length < 2) return;
    setAiLoading(true); setPendingEdges([]);
    try {
      const { data } = await axios.post(
        `${BASE}/api/tools/genius-labs/connect`,
        { ideas: nodes.map((n) => ({ id: n.id, title: n.title, body: n.body })) },
        { headers: authHeader }
      );
      const existing = new Set(edges.map((e) => [e.fromId, e.toId].sort().join("|")));
      const fresh = (data.connections as any[]).filter((c) => !existing.has([c.from_id, c.to_id].sort().join("|")));
      setPendingEdges(fresh.map((c) => ({ fromId: c.from_id, toId: c.to_id, label: c.label, reason: c.reason })));
    } catch (err: any) {
      alert(`AI connect failed: ${err?.response?.data?.detail || err?.message}`);
    } finally { setAiLoading(false); }
  };

  const acceptPending = (p: typeof pendingEdges[0]) => {
    const edge = addEdge(projectId, p.fromId, p.toId, p.label, p.reason, true);
    setEdges((prev) => [...prev, edge]);
    setPendingEdges((prev) => prev.filter((x) => !(x.fromId === p.fromId && x.toId === p.toId)));
  };
  const rejectPending = (p: typeof pendingEdges[0]) => {
    setPendingEdges((prev) => prev.filter((x) => !(x.fromId === p.fromId && x.toId === p.toId)));
  };
  const acceptAllPending = () => {
    pendingEdges.forEach((p) => {
      const edge = addEdge(projectId, p.fromId, p.toId, p.label, p.reason, true);
      setEdges((prev) => [...prev, edge]);
    });
    setPendingEdges([]);
  };

  // ── JSON file import ──────────────────────────────────────────────────────
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const json = JSON.parse(ev.target?.result as string);
        setImportError(null);
        setImportedCount(null);

        // Detect chatbot exports
        const isChatGPT = Array.isArray(json) && json.length > 0 && json[0].mapping !== undefined;
        const isOpenWebUI = Array.isArray(json) && json.length > 0 && json[0].chat?.messages !== undefined;

        if (isChatGPT || isOpenWebUI) {
          const convs = isChatGPT ? parseChatGPTExport(json) : parseOpenWebUIExport(json);
          if (convs.length === 0) { setImportError("No conversations found in this chatbot export."); return; }
          setConversations(convs);
          setSelectedConvIds(new Set(convs.map((c) => c.id)));
        } else {
          // Own ideas: array of { title, body?, text?, tags? }
          const items = Array.isArray(json) ? json : [json];
          const valid = items.filter((item: any) => item && typeof item.title === "string" && item.title.trim());
          if (valid.length === 0) { setImportError("No ideas found. Expected JSON array with objects containing a \"title\" field."); return; }
          const newNodes: IdeaNode[] = [];
          for (const item of valid) {
            const body = (item.body ?? item.text ?? item.description ?? "").toString().trim();
            const tags = Array.isArray(item.tags) ? item.tags.map(String) : [];
            const node = addIdea(projectId, item.title.trim(), body, tags);
            newNodes.push(node);
          }
          setNodes((prev) => [...prev, ...newNodes]);
          setImportedCount(valid.length);
        }
      } catch {
        setImportError("Invalid JSON file.");
      }
    };
    reader.readAsText(file);
    // Reset input so same file can be re-uploaded
    e.target.value = "";
  };

  const toggleConv = (id: string) => {
    setSelectedConvIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleGenerateMindmap = async () => {
    const selected = conversations.filter((c) => selectedConvIds.has(c.id));
    if (selected.length === 0) return;

    const fullText = selected.map((c) => `=== ${c.title} ===\n${c.text}`).join("\n\n");

    setImportLoading(true); setImportError(null);
    try {
      const res = await fetch(`${BASE}/api/tools/genius-labs/mindmap`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeader },
        body: JSON.stringify({ text: fullText }),
      });
      if (!res.ok) throw new Error(`Server error ${res.status}`);
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      const mm: MindmapData = {
        root: data.root || "My Ideas",
        branches: data.branches || [],
        generatedAt: new Date().toISOString(),
        sourceTitle: selected.length === 1 ? selected[0].title : `${selected.length} conversations`,
      };
      saveMindmap(projectId, mm);
      setMindmap(mm);
      setView("mindmap");
    } catch (err: any) {
      setImportError(err.message || "Failed to generate mindmap.");
    } finally { setImportLoading(false); }
  };

  // Generate mindmap from existing idea nodes using local Ollama
  const handleMindmapFromIdeas = async () => {
    if (nodes.length < 3) return;
    const text = nodes.map((n) => `- ${n.title}: ${n.body}`).join("\n");
    setImportLoading(true); setImportError(null);
    try {
      const res = await fetch(`${BASE}/api/tools/genius-labs/mindmap`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeader },
        body: JSON.stringify({ text }),
      });
      if (!res.ok) throw new Error(`Server error ${res.status}`);
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      const mm: MindmapData = { root: data.root || "My Ideas", branches: data.branches || [], generatedAt: new Date().toISOString() };
      saveMindmap(projectId, mm);
      setMindmap(mm);
      setView("mindmap");
    } catch (err: any) {
      alert(err.message || "Failed to generate mindmap.");
    } finally { setImportLoading(false); }
  };

  // ── Settings ──────────────────────────────────────────────────────────────
  const handleSaveKey = () => {
    setOpenAIKey(apiKey);
    setKeySaved(true);
    setTimeout(() => setKeySaved(false), 2000);
  };

  // ── Graph interactions ────────────────────────────────────────────────────
  const svgPoint = (e: React.MouseEvent) => {
    const rect = svgRef.current!.getBoundingClientRect();
    return { x: (e.clientX - rect.left - pan.x) / zoom, y: (e.clientY - rect.top - pan.y) / zoom };
  };

  const handleNodeMouseDown = (e: React.MouseEvent, id: string) => {
    if (linking && linking !== id) {
      const edge = addEdge(projectId, linking, id, "related to", "", false);
      setEdges((prev) => [...prev, edge]);
      setLinking(null); return;
    }
    e.stopPropagation();
    setSelectedNode(id); setSelectedEdge(null);
    const pt = svgPoint(e);
    const node = simNodesRef.current.find((n) => n.id === id);
    if (!node) return;
    dragOffset.current = { x: pt.x - node.x, y: pt.y - node.y };
    setDragging(id);
  };

  const handleSvgMouseMove = (e: React.MouseEvent) => {
    if (dragging) {
      const pt = svgPoint(e);
      const idx = simNodesRef.current.findIndex((n) => n.id === dragging);
      if (idx !== -1) {
        simNodesRef.current[idx].x = pt.x - dragOffset.current.x;
        simNodesRef.current[idx].y = pt.y - dragOffset.current.y;
        simNodesRef.current[idx].vx = 0; simNodesRef.current[idx].vy = 0;
        setSimNodes([...simNodesRef.current]);
      }
    } else if (panStart.current) {
      setPan({ x: panStart.current.px + e.clientX - panStart.current.mx, y: panStart.current.py + e.clientY - panStart.current.my });
    }
  };

  const handleSvgMouseUp = () => {
    if (dragging) saveNodePositions(projectId, simNodesRef.current.map((n) => ({ id: n.id, x: n.x, y: n.y })));
    setDragging(null); panStart.current = null;
  };

  const handleSvgMouseDown = (e: React.MouseEvent) => {
    if ((e.target as SVGElement).tagName === "svg") {
      setSelectedNode(null); setSelectedEdge(null); setLinking(null);
      panStart.current = { mx: e.clientX, my: e.clientY, px: pan.x, py: pan.y };
    }
  };

  const edgePath = (e: IdeaEdge) => {
    const a = simNodes.find((n) => n.id === e.fromId), b = simNodes.find((n) => n.id === e.toId);
    if (!a || !b) return "";
    const dx = b.x - a.x, dy = b.y - a.y, dist = Math.sqrt(dx * dx + dy * dy) || 1;
    const ux = dx / dist, uy = dy / dist;
    return `M ${a.x + ux * NODE_R} ${a.y + uy * NODE_R} L ${b.x - ux * NODE_R} ${b.y - uy * NODE_R}`;
  };

  const edgeMid = (e: IdeaEdge) => {
    const a = simNodes.find((n) => n.id === e.fromId), b = simNodes.find((n) => n.id === e.toId);
    return a && b ? { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 } : null;
  };

  // ── Mindmap interactions ──────────────────────────────────────────────────
  const { mapNodes, mapEdges } = mindmap
    ? layoutMindmap(mindmap, 500, 350)
    : { mapNodes: [], mapEdges: [] };

  const visibleMapNodes = mapNodes.filter((n) => {
    if (n.depth === 0 || n.depth === 1) return true;
    // depth 2: visible only if parent branch not collapsed
    const branchId = n.id.replace(/c\d+$/, "");
    return !collapsedBranches.has(branchId);
  });
  const visibleMapEdges = mapEdges.filter((e) => {
    const toNode = mapNodes.find((n) => n.id === e.to);
    return toNode ? visibleMapNodes.some((n) => n.id === e.to) : false;
  });

  const curvePath = (fromId: string, toId: string) => {
    const a = mapNodes.find((n) => n.id === fromId), b = mapNodes.find((n) => n.id === toId);
    if (!a || !b) return "";
    const mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2;
    return `M ${a.x} ${a.y} Q ${mx} ${my} ${b.x} ${b.y}`;
  };

  const nodeRadius = (depth: number) => depth === 0 ? 52 : depth === 1 ? 40 : 28;

  const handleMapWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    setMapZoom((z) => Math.min(3, Math.max(0.2, z - e.deltaY * 0.001)));
  };

  const handleMapMouseDown = (e: React.MouseEvent) => {
    if ((e.target as SVGElement).tagName === "svg") {
      mapPanStart.current = { mx: e.clientX, my: e.clientY, px: mapPan.x, py: mapPan.y };
    }
  };

  const handleMapMouseMove = (e: React.MouseEvent) => {
    if (mapPanStart.current) {
      setMapPan({ x: mapPanStart.current.px + e.clientX - mapPanStart.current.mx, y: mapPanStart.current.py + e.clientY - mapPanStart.current.my });
    }
  };

  const handleMapMouseUp = () => { mapPanStart.current = null; };

  // ── Render ────────────────────────────────────────────────────────────────
  const VIEWS: { id: View; label: string }[] = [
    { id: "capture", label: "Capture" },
    { id: "graph", label: "Graph" },
    { id: "mindmap", label: "Mindmap" },
    ...(showSkillLensNotes ? [{ id: "notes" as View, label: "Skill Lens Notes" }] : []),
    { id: "settings", label: "Settings" },
  ];

  return (
    <div className="flex flex-col h-full">
    {showSkillLensNotes && <CCBSkillHint tab="genius-labs" className="mb-4" />}
      {/* Header */}
      <div className="shrink-0 border-b border-border px-4 py-2.5 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-accent" />
          <div>
            <span className="text-sm font-semibold text-foreground">Genius Labs</span>
            <p className="text-[10px] text-muted-foreground leading-tight">
              Capture ideas · find hidden connections · build visual knowledge maps
            </p>
          </div>
          {nodes.length > 0 && (
            <span className="text-[11px] text-muted-foreground ml-1">{nodes.length} ideas · {edges.length} connections</span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {VIEWS.map((v) => (
            <button key={v.id} onClick={() => setView(v.id)}
              className={`px-3 py-1 text-[11px] font-mono uppercase tracking-wider rounded transition-colors ${
                view === v.id ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"
              }`}
            >
              {v.id === "settings" ? <Settings className="h-3.5 w-3.5" /> : v.label}
            </button>
          ))}
        </div>
      </div>

      {/* ══ CAPTURE ══════════════════════════════════════════════════════════ */}
      {view === "capture" && (
        <div className="flex flex-col sm:flex-row flex-1 min-h-0 overflow-hidden">
          {/* Left panel */}
          <div className="w-full sm:w-80 shrink-0 border-b sm:border-b-0 sm:border-r border-border flex flex-col overflow-y-auto">
            {/* Quick jot */}
            <div className="p-4 space-y-2.5 border-b border-border">
              <p className="text-[11px] font-mono text-muted-foreground uppercase tracking-wider">New Idea</p>
              <input
                className="w-full bg-background border border-border rounded px-2.5 py-1.5 text-sm placeholder:text-muted-foreground/50 focus:outline-none focus:border-accent/50"
                placeholder="Idea title…"
                value={captureTitle}
                onChange={(e) => setCaptureTitle(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && handleAdd()}
              />
              <textarea
                className="w-full bg-background border border-border rounded px-2.5 py-1.5 text-sm placeholder:text-muted-foreground/50 focus:outline-none focus:border-accent/50 resize-none min-h-[70px]"
                placeholder="Expand on this… (optional)"
                value={captureBody}
                onChange={(e) => setCaptureBody(e.target.value)}
              />
              <div className="flex items-center gap-1.5">
                <Tag className="h-3 w-3 text-muted-foreground shrink-0" />
                <input
                  className="flex-1 bg-background border border-border rounded px-2 py-1 text-xs placeholder:text-muted-foreground/50 focus:outline-none focus:border-accent/50"
                  placeholder="tags, comma separated"
                  value={captureTags}
                  onChange={(e) => setCaptureTags(e.target.value)}
                />
              </div>
              <button onClick={handleAdd} disabled={!captureTitle.trim()}
                className="w-full flex items-center justify-center gap-1.5 bg-accent text-accent-foreground rounded px-3 py-1.5 text-xs font-semibold hover:bg-accent/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Plus className="h-3.5 w-3.5" /> Add Idea
              </button>
            </div>

            {/* Ollama AI connect */}
            <div className="p-4 space-y-2 border-b border-border">
              <p className="text-[11px] font-mono text-muted-foreground uppercase tracking-wider">Connect Ideas (Local AI)</p>
              <button onClick={handleAiConnect} disabled={nodes.length < 2 || aiLoading}
                className="w-full flex items-center justify-center gap-1.5 border border-border rounded px-3 py-1.5 text-xs font-medium hover:border-accent/50 hover:text-accent transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {aiLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Network className="h-3.5 w-3.5" />}
                Find Connections (Ollama)
              </button>
              {nodes.length >= 3 && (
                <button onClick={handleMindmapFromIdeas} disabled={importLoading}
                  className="w-full flex items-center justify-center gap-1.5 border border-border rounded px-3 py-1.5 text-xs font-medium hover:border-accent/50 hover:text-accent transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {importLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <BrainCircuit className="h-3.5 w-3.5" />}
                  Build Mindmap from Ideas
                </button>
              )}
            </div>

            {/* JSON import */}
            <div className="p-4 space-y-2">
              <p className="text-[11px] font-mono text-muted-foreground uppercase tracking-wider flex items-center gap-1">
                <FileJson className="h-3 w-3" /> Import JSON
              </p>
              <p className="text-[10px] text-muted-foreground leading-relaxed">
                Upload your own ideas as <span className="font-mono">[{"{"}"title":"...","body":"..."{"}"}]</span>, or a chatbot export (ChatGPT <span className="font-mono">conversations.json</span>).
              </p>
              <input ref={fileRef} type="file" accept=".json" className="hidden" onChange={handleFileChange} />
              <button onClick={() => fileRef.current?.click()}
                className="w-full flex items-center justify-center gap-1.5 border border-border rounded px-3 py-1.5 text-xs font-medium hover:border-accent/50 hover:text-accent transition-colors"
              >
                <Upload className="h-3.5 w-3.5" /> Choose JSON file
              </button>
              {importError && <p className="text-[11px] text-destructive">{importError}</p>}
              {importedCount !== null && (
                <p className="text-[11px] text-emerald-600">{importedCount} idea{importedCount !== 1 ? "s" : ""} added to graph.</p>
              )}

              {conversations.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-[10px] text-muted-foreground">{conversations.length} conversations found</p>
                    <div className="flex gap-2">
                      <button onClick={() => setSelectedConvIds(new Set(conversations.map((c) => c.id)))}
                        className="text-[10px] text-accent hover:underline">All</button>
                      <button onClick={() => setSelectedConvIds(new Set())}
                        className="text-[10px] text-muted-foreground hover:underline">None</button>
                    </div>
                  </div>
                  <div className="max-h-48 overflow-y-auto space-y-1 border border-border rounded p-1.5">
                    {conversations.map((c) => (
                      <label key={c.id} className="flex items-center gap-2 cursor-pointer hover:bg-secondary/30 rounded px-1.5 py-0.5">
                        <input type="checkbox" checked={selectedConvIds.has(c.id)} onChange={() => toggleConv(c.id)}
                          className="h-3 w-3 rounded border-border" />
                        <span className="text-[11px] text-card-foreground truncate">{c.title}</span>
                      </label>
                    ))}
                  </div>
                  <button
                    onClick={handleGenerateMindmap}
                    disabled={selectedConvIds.size === 0 || importLoading}
                    className="w-full flex items-center justify-center gap-1.5 bg-accent text-accent-foreground rounded px-3 py-1.5 text-xs font-semibold hover:bg-accent/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {importLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                    Generate Mindmap ({selectedConvIds.size})
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Right: ideas list */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 min-w-0">
            {pendingEdges.length > 0 && (
              <div className="border border-accent/30 rounded-md bg-accent/5 p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-[11px] font-mono text-accent uppercase tracking-wider flex items-center gap-1">
                    <Sparkles className="h-3 w-3" /> {pendingEdges.length} suggested connections
                  </p>
                  <button onClick={acceptAllPending} className="text-[11px] font-medium text-accent hover:underline">Accept all</button>
                </div>
                {pendingEdges.map((p, i) => {
                  const from = nodes.find((n) => n.id === p.fromId), to = nodes.find((n) => n.id === p.toId);
                  if (!from || !to) return null;
                  return (
                    <div key={i} className="border border-border rounded bg-card px-3 py-2 flex items-start gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-card-foreground">
                          <span className="font-semibold" style={{ color: from.color }}>{from.title}</span>
                          {" "}<span className="text-muted-foreground font-mono text-[10px] border border-border rounded px-1.5 py-0.5">{p.label}</span>{" "}
                          <span className="font-semibold" style={{ color: to.color }}>{to.title}</span>
                        </p>
                        {p.reason && <p className="text-[11px] text-muted-foreground mt-0.5">{p.reason}</p>}
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <button onClick={() => acceptPending(p)} className="p-1 rounded hover:bg-emerald-500/10 text-emerald-600"><Check className="h-3.5 w-3.5" /></button>
                        <button onClick={() => rejectPending(p)} className="p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive"><X className="h-3.5 w-3.5" /></button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {nodes.length === 0 && (
              <div className="flex flex-col gap-4 p-6">
                <div className="bg-accent/10 border border-accent/20 rounded-lg p-5 space-y-3">
                  <div className="flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-accent" />
                    <p className="text-sm font-semibold text-foreground">What is Genius Labs?</p>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    A free-thinking workspace for when you're not ready to write a requirement yet.
                    Dump raw ideas here — then let AI find the connections, group the themes,
                    and turn the mess into a structured mindmap you can actually use.
                  </p>
                  <div className="space-y-2 pt-1">
                    {[
                      { step: "1", label: "Capture", desc: "Add any idea — a question, a risk, a feature fragment, a stakeholder concern." },
                      { step: "2", label: "Connect", desc: "AI scans all your ideas and surfaces hidden relationships between them." },
                      { step: "3", label: "Map", desc: "Generate a visual mindmap that organizes everything into branches and themes." },
                    ].map((s) => (
                      <div key={s.step} className="flex gap-3 items-start">
                        <div className="w-5 h-5 rounded-full bg-accent text-accent-foreground text-[10px] font-bold flex items-center justify-center shrink-0 mt-0.5">
                          {s.step}
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-foreground">{s.label}</p>
                          <p className="text-[11px] text-muted-foreground leading-relaxed">{s.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-[11px] text-muted-foreground/70 italic pt-1 border-t border-border">
                    Start by adding your first idea in the panel on the left.
                  </p>
                </div>
              </div>
            )}

            <div className="space-y-2">
              {[...nodes].reverse().map((node) => (
                <div key={node.id} className="border border-border rounded-md bg-card px-3 py-2.5 flex items-start gap-3 group"
                  style={{ borderLeftColor: node.color, borderLeftWidth: 3 }}>
                  {editId === node.id ? (
                    <div className="flex-1 space-y-1.5">
                      <input autoFocus className="w-full bg-background border border-border rounded px-2 py-1 text-sm focus:outline-none focus:border-accent/50"
                        value={editTitle} onChange={(e) => setEditTitle(e.target.value)} />
                      <textarea className="w-full bg-background border border-border rounded px-2 py-1 text-xs resize-none min-h-[50px] focus:outline-none focus:border-accent/50"
                        value={editBody} onChange={(e) => setEditBody(e.target.value)} />
                      <div className="flex gap-1.5">
                        <button onClick={handleEditSave} className="flex items-center gap-1 text-[11px] font-medium text-emerald-600 hover:underline"><Check className="h-3 w-3" /> Save</button>
                        <button onClick={() => setEditId(null)} className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"><X className="h-3 w-3" /> Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-card-foreground">{node.title}</p>
                      {node.body && <p className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed">{node.body}</p>}
                      {node.tags.length > 0 && (
                        <div className="flex gap-1 mt-1.5 flex-wrap">
                          {node.tags.map((t) => <span key={t} className="text-[10px] border border-border rounded px-1.5 py-0.5 text-muted-foreground">{t}</span>)}
                        </div>
                      )}
                      {edges.filter((e) => e.fromId === node.id || e.toId === node.id).length > 0 && (
                        <div className="mt-1.5 flex flex-wrap gap-1">
                          {edges.filter((e) => e.fromId === node.id || e.toId === node.id).map((e) => {
                            const otherId = e.fromId === node.id ? e.toId : e.fromId;
                            const other = nodes.find((n) => n.id === otherId);
                            if (!other) return null;
                            return (
                              <span key={e.id} className="flex items-center gap-1 text-[10px] text-muted-foreground border border-border rounded px-1.5 py-0.5">
                                <ChevronRight className="h-2.5 w-2.5" />
                                <span className="font-mono">{e.label}</span>
                                <span style={{ color: other.color }}>{other.title}</span>
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                  {editId !== node.id && (
                    <div className="shrink-0 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={() => { setEditId(node.id); setEditTitle(node.title); setEditBody(node.body); }}
                        className="p-1 rounded hover:bg-secondary text-muted-foreground hover:text-foreground"><Pencil className="h-3.5 w-3.5" /></button>
                      <button onClick={() => handleDelete(node.id)}
                        className="p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ══ GRAPH ════════════════════════════════════════════════════════════ */}
      {view === "graph" && (
        <div className="flex flex-1 min-h-0 overflow-hidden relative">
          {/* Toolbar */}
          <div className="absolute top-3 right-3 z-10 flex flex-col gap-1.5">
            {[{ icon: ZoomIn, action: () => setZoom((z) => Math.min(3, z + 0.15)), title: "Zoom in" },
              { icon: ZoomOut, action: () => setZoom((z) => Math.max(0.3, z - 0.15)), title: "Zoom out" },
              { icon: RotateCcw, action: () => { setZoom(1); setPan({ x: 0, y: 0 }); }, title: "Reset" }]
              .map(({ icon: Icon, action, title }) => (
                <button key={title} onClick={action} title={title}
                  className="p-1.5 bg-card border border-border rounded shadow-sm hover:border-accent/50 text-muted-foreground hover:text-foreground transition-colors">
                  <Icon className="h-3.5 w-3.5" />
                </button>
              ))}
            <button onClick={() => setSimRunning((r) => !r)}
              className={`p-1.5 bg-card border rounded shadow-sm text-xs font-mono transition-colors ${simRunning ? "border-accent/50 text-accent" : "border-border text-muted-foreground"}`}>
              {simRunning ? "⏸" : "▶"}
            </button>
            {linking && (
              <button onClick={() => setLinking(null)} className="p-1.5 bg-destructive/10 border border-destructive/30 rounded shadow-sm text-destructive">
                <Unlink className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          {selectedNode && (
            <div className="absolute top-3 left-3 z-10 bg-card border border-border rounded-md shadow-md p-2 space-y-1">
              <p className="text-[10px] font-mono text-muted-foreground uppercase px-1">{nodes.find((n) => n.id === selectedNode)?.title}</p>
              <button onClick={() => { setLinking(selectedNode); setSelectedNode(null); }}
                className="flex items-center gap-1.5 text-xs px-2 py-1 rounded hover:bg-secondary text-muted-foreground hover:text-foreground w-full">
                <Link className="h-3 w-3" /> Link to…
              </button>
              <button onClick={() => { const n = nodes.find((n) => n.id === selectedNode); if (n) { setEditId(n.id); setEditTitle(n.title); setEditBody(n.body); setView("capture"); } }}
                className="flex items-center gap-1.5 text-xs px-2 py-1 rounded hover:bg-secondary text-muted-foreground hover:text-foreground w-full">
                <Pencil className="h-3 w-3" /> Edit
              </button>
              <button onClick={() => { handleDelete(selectedNode); setSelectedNode(null); }}
                className="flex items-center gap-1.5 text-xs px-2 py-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive w-full">
                <Trash2 className="h-3 w-3" /> Delete
              </button>
            </div>
          )}

          {selectedEdge && (
            <div className="absolute top-3 left-3 z-10 bg-card border border-border rounded-md shadow-md p-2 space-y-1.5">
              <p className="text-[10px] font-mono text-muted-foreground uppercase px-1">Connection: {edges.find((e) => e.id === selectedEdge)?.label}</p>
              {edges.find((e) => e.id === selectedEdge)?.reason && (
                <p className="text-[11px] text-muted-foreground px-1 max-w-[200px]">{edges.find((e) => e.id === selectedEdge)?.reason}</p>
              )}
              <button onClick={() => { deleteEdge(projectId, selectedEdge); setEdges((prev) => prev.filter((e) => e.id !== selectedEdge)); setSelectedEdge(null); }}
                className="flex items-center gap-1.5 text-xs px-2 py-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive w-full">
                <Trash2 className="h-3 w-3" /> Remove
              </button>
            </div>
          )}

          {linking && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 bg-accent text-accent-foreground text-xs font-medium px-3 py-1.5 rounded-full shadow-md">
              Click another node to connect
            </div>
          )}

          {nodes.length === 0 && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-center pointer-events-none">
              <Network className="h-12 w-12 text-muted-foreground/15" />
              <p className="text-sm font-semibold text-muted-foreground">Nothing to graph yet</p>
              <p className="text-xs text-muted-foreground max-w-xs">Go to <span className="font-semibold">Capture</span>, add at least 2 ideas, then use <span className="font-semibold">Find Connections</span> to let AI draw the links.</p>
            </div>
          )}

          <svg ref={svgRef} className="flex-1 w-full h-full select-none"
            style={{ cursor: linking ? "crosshair" : dragging ? "grabbing" : "grab" }}
            onMouseDown={handleSvgMouseDown} onMouseMove={handleSvgMouseMove}
            onMouseUp={handleSvgMouseUp} onMouseLeave={handleSvgMouseUp}
            onWheel={(e) => { e.preventDefault(); setZoom((z) => Math.min(3, Math.max(0.3, z - e.deltaY * 0.001))); }}>
            <defs>
              <marker id="arrowhead" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto">
                <polygon points="0 0, 8 3, 0 6" fill="#94a3b8" />
              </marker>
            </defs>
            <g transform={`translate(${pan.x},${pan.y}) scale(${zoom})`}>
              {graphEdges.map((e) => {
                const path = edgePath(e), mid = edgeMid(e);
                if (!path || !mid) return null;
                const isSel = selectedEdge === e.id;
                return (
                  <g key={e.id} onClick={() => { setSelectedEdge(e.id); setSelectedNode(null); }} style={{ cursor: "pointer" }}>
                    <path d={path} stroke="transparent" strokeWidth={12} fill="none" />
                    <path d={path} stroke={isSel ? "hsl(var(--accent))" : e.aiGenerated ? "#818cf8" : "#94a3b8"}
                      strokeWidth={isSel ? 2 : 1.5} fill="none"
                      strokeDasharray={e.aiGenerated ? "5,3" : undefined}
                      markerEnd="url(#arrowhead)" />
                    {e.label && (
                      <text x={mid.x} y={mid.y - 6} textAnchor="middle" fontSize={10}
                        fill={isSel ? "hsl(var(--accent))" : "#94a3b8"} className="pointer-events-none">{e.label}</text>
                    )}
                  </g>
                );
              })}
              {simNodes.map((node) => {
                const isSel = selectedNode === node.id, isLink = linking === node.id;
                return (
                  <g key={node.id} transform={`translate(${node.x},${node.y})`}
                    onMouseDown={(e) => handleNodeMouseDown(e, node.id)}
                    style={{ cursor: linking ? "pointer" : "grab" }}>
                    {(isSel || isLink) && <circle r={NODE_R + 5} fill="none"
                      stroke={isLink ? "#f59e0b" : "hsl(var(--accent))"} strokeWidth={2} opacity={0.5} />}
                    <circle r={NODE_R} fill={node.color} fillOpacity={0.15}
                      stroke={isSel ? "hsl(var(--accent))" : node.color} strokeWidth={isSel ? 2.5 : 1.5} />
                    {wrapText(node.title, 12).map((line, i, arr) => (
                      <text key={i} textAnchor="middle" fontSize={11} fontWeight={600} fill={node.color}
                        y={(i - (arr.length - 1) / 2) * 14} className="pointer-events-none">{line}</text>
                    ))}
                  </g>
                );
              })}
            </g>
          </svg>
        </div>
      )}

      {/* ══ MINDMAP ══════════════════════════════════════════════════════════ */}
      {view === "mindmap" && (
        <div className="flex-1 min-h-0 overflow-hidden relative">
          {/* Mindmap toolbar */}
          <div className="absolute top-3 right-3 z-10 flex flex-col gap-1.5">
            <button onClick={() => setMapZoom((z) => Math.min(3, z + 0.15))} title="Zoom in"
              className="p-1.5 bg-card border border-border rounded shadow-sm hover:border-accent/50 text-muted-foreground hover:text-foreground transition-colors">
              <ZoomIn className="h-3.5 w-3.5" />
            </button>
            <button onClick={() => setMapZoom((z) => Math.max(0.2, z - 0.15))} title="Zoom out"
              className="p-1.5 bg-card border border-border rounded shadow-sm hover:border-accent/50 text-muted-foreground hover:text-foreground transition-colors">
              <ZoomOut className="h-3.5 w-3.5" />
            </button>
            <button onClick={() => { setMapZoom(1); setMapPan({ x: 0, y: 0 }); }} title="Reset"
              className="p-1.5 bg-card border border-border rounded shadow-sm hover:border-accent/50 text-muted-foreground hover:text-foreground transition-colors">
              <RotateCcw className="h-3.5 w-3.5" />
            </button>
          </div>

          {!mindmap && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-center">
              <BrainCircuit className="h-12 w-12 text-muted-foreground/15" />
              <p className="text-sm font-semibold text-muted-foreground">No mindmap yet</p>
              <p className="text-xs text-muted-foreground max-w-xs">
                Import your ChatGPT conversations in the Capture tab, or build one from your existing ideas.
              </p>
              <button onClick={() => setView("capture")}
                className="text-xs text-accent hover:underline flex items-center gap-1">
                <Sparkles className="h-3 w-3" /> Go to Capture
              </button>
            </div>
          )}

          {mindmap && (
            <>
              <div className="absolute top-3 left-3 z-10 bg-card border border-border rounded-md px-3 py-1.5 shadow-sm space-y-0.5">
                <p className="text-[11px] font-semibold text-card-foreground">{mindmap.root}</p>
                {mindmap.sourceTitle && <p className="text-[10px] text-muted-foreground">from: {mindmap.sourceTitle}</p>}
                <p className="text-[10px] text-muted-foreground">
                  {mindmap.branches.length} branches · {mindmap.branches.reduce((s, b) => s + b.children.length, 0)} ideas
                </p>
                <p className="text-[10px] text-muted-foreground/60">Click branch to collapse · Drag to pan</p>
              </div>

              <svg className="w-full h-full select-none" style={{ cursor: mapPanStart.current ? "grabbing" : "grab" }}
                onWheel={handleMapWheel} onMouseDown={handleMapMouseDown}
                onMouseMove={handleMapMouseMove} onMouseUp={handleMapMouseUp} onMouseLeave={handleMapMouseUp}>
                <g transform={`translate(${mapPan.x},${mapPan.y}) scale(${mapZoom})`}>
                  {/* Edges */}
                  {visibleMapEdges.map((e, i) => {
                    const toNode = mapNodes.find((n) => n.id === e.to);
                    return (
                      <path key={i} d={curvePath(e.from, e.to)}
                        stroke={toNode?.color ?? "#94a3b8"} strokeWidth={toNode?.depth === 1 ? 2 : 1.5}
                        fill="none" opacity={0.5} />
                    );
                  })}
                  {/* Nodes */}
                  {visibleMapNodes.map((n) => {
                    const r = nodeRadius(n.depth);
                    const isHovered = hoveredMapNode === n.id;
                    const isBranch = n.depth === 1;
                    const isCollapsed = collapsedBranches.has(n.id);
                    return (
                      <g key={n.id} transform={`translate(${n.x},${n.y})`}
                        onMouseEnter={() => setHoveredMapNode(n.id)}
                        onMouseLeave={() => setHoveredMapNode(null)}
                        onClick={() => isBranch && setCollapsedBranches((prev) => {
                          const next = new Set(prev);
                          if (next.has(n.id)) next.delete(n.id); else next.add(n.id);
                          return next;
                        })}
                        style={{ cursor: isBranch ? "pointer" : "default" }}>
                        <circle r={isHovered ? r + 4 : r}
                          fill={n.depth === 0 ? "#1e293b" : n.color}
                          fillOpacity={n.depth === 0 ? 1 : 0.15}
                          stroke={n.color} strokeWidth={n.depth === 0 ? 0 : 1.5}
                          style={{ transition: "r 0.15s" }} />
                        {/* Collapse indicator */}
                        {isBranch && (
                          <text x={r - 2} y={-r + 4} fontSize={8} fill={n.color} textAnchor="middle">
                            {isCollapsed ? "+" : "−"}
                          </text>
                        )}
                        {wrapText(n.label, n.depth === 0 ? 14 : n.depth === 1 ? 10 : 9).map((line, i, arr) => (
                          <text key={i} textAnchor="middle" fontSize={n.depth === 0 ? 13 : n.depth === 1 ? 11 : 9}
                            fontWeight={n.depth <= 1 ? 700 : 500}
                            fill={n.depth === 0 ? "#fff" : n.color}
                            y={(i - (arr.length - 1) / 2) * (n.depth === 0 ? 16 : 13)}
                            className="pointer-events-none">
                            {line}
                          </text>
                        ))}
                        {/* Tooltip on hover for leaf notes */}
                        {isHovered && n.note && n.depth === 2 && (
                          <foreignObject x={r + 6} y={-24} width={180} height={60} className="pointer-events-none">
                            <div className="bg-card border border-border rounded px-2 py-1.5 text-[10px] text-muted-foreground shadow-md leading-relaxed">
                              {n.note}
                            </div>
                          </foreignObject>
                        )}
                      </g>
                    );
                  })}
                </g>
              </svg>
            </>
          )}
        </div>
      )}

      {/* ══ CCB NOTES ════════════════════════════════════════════════════════ */}
      {view === "notes" && (() => {
        const prefix = `ccb-note:${projectId}:`;
        const saved: (CCBSavedNote & { key: string })[] = [];
        for (let i = 0; i < localStorage.length; i++) {
          const k = localStorage.key(i);
          if (k && k.startsWith(prefix)) {
            try {
              const v: CCBSavedNote = JSON.parse(localStorage.getItem(k)!);
              saved.push({ ...v, key: k });
            } catch {}
          }
        }
        saved.sort((a, b) => b.savedAt.localeCompare(a.savedAt));

        function clearNote(key: string) {
          localStorage.removeItem(key);
          setView("capture"); setTimeout(() => setView("notes"), 0); // force re-render
        }

        return (
          <div className="flex-1 overflow-y-auto p-5">
            <div className="flex items-center gap-2 mb-4">
              <BookText className="h-4 w-4" style={{ color: "#314E6E" }} />
              <span className="text-sm font-semibold" style={{ color: "#1E1E1C" }}>Skill Lens Notes</span>
              <span className="text-[11px] font-mono ml-1" style={{ color: "#5C5C57" }}>
                {saved.length} saved across tabs
              </span>
            </div>

            {saved.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
                <BookText className="h-8 w-8" style={{ color: "#D9D4CC" }} />
                <p className="text-[13px]" style={{ color: "#5C5C57" }}>No notes saved yet.</p>
                <p className="text-[11px]" style={{ color: "#5C5C57", maxWidth: 280, lineHeight: 1.6 }}>
                  Open any tab, expand Skill Lens, write in "Your notes", then click "Save for later →".
                </p>
              </div>
            ) : (
              <div className="space-y-3 max-w-2xl">
                {saved.map(note => (
                  <div key={note.key} className="rounded-xl p-4"
                    style={{ backgroundColor: "#FCFBF8", border: "1px solid #D9D4CC" }}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 rounded"
                        style={{ backgroundColor: "#314E6E22", color: "#314E6E" }}>
                        {note.tab}
                      </span>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-mono" style={{ color: "#A37B32" }}>
                          {new Date(note.savedAt).toLocaleDateString()}
                        </span>
                        <button
                          onClick={() => clearNote(note.key)}
                          className="text-[10px] font-mono hover:underline"
                          style={{ color: "#6A3B3B" }}
                        >
                          clear
                        </button>
                      </div>
                    </div>
                    <p className="whitespace-pre-wrap leading-relaxed" style={{ fontSize: 13, color: "#1E1E1C" }}>
                      {note.notes}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {/* ══ SETTINGS ═════════════════════════════════════════════════════════ */}
      {view === "settings" && (
        <div className="flex-1 overflow-y-auto p-6 max-w-md space-y-6">
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-0.5">OpenAI API Key</h3>
            <p className="text-[11px] text-muted-foreground mb-3">
              Used for generating mindmaps from ChatGPT conversations. Stored locally in your browser — never sent to our servers.
              Uses <span className="font-mono">gpt-4o-mini</span> (low cost, ~$0.01–0.05 per import).
            </p>
            <div className="flex items-center gap-2">
              <div className="flex-1 relative">
                <input
                  type={showKey ? "text" : "password"}
                  className="w-full bg-background border border-border rounded px-2.5 py-1.5 text-sm font-mono placeholder:text-muted-foreground/50 focus:outline-none focus:border-accent/50 pr-8"
                  placeholder="sk-..."
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                />
                <button onClick={() => setShowKey((s) => !s)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
                  {showKey ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                </button>
              </div>
              <button onClick={handleSaveKey}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-accent text-accent-foreground rounded text-xs font-semibold hover:bg-accent/90 transition-colors">
                {keySaved ? <Check className="h-3.5 w-3.5" /> : null}
                {keySaved ? "Saved!" : "Save"}
              </button>
            </div>
            <p className="text-[10px] text-muted-foreground mt-2">
              Get a key at <span className="font-mono text-accent">platform.openai.com/api-keys</span>
            </p>
          </div>

          <div className="border-t border-border pt-4">
            <h3 className="text-sm font-semibold text-foreground mb-1">Data</h3>
            <p className="text-[11px] text-muted-foreground mb-3">All ideas, connections, and mindmaps are stored locally in your browser (localStorage). Nothing is uploaded to our servers.</p>
            <button
              onClick={() => {
                if (!window.confirm("Clear all Genius Labs data for this project? This cannot be undone.")) return;
                localStorage.removeItem(`genius-labs:${projectId}`);
                setNodes([]); setEdges([]); setMindmap(null);
              }}
              className="flex items-center gap-1.5 text-xs text-destructive border border-destructive/30 rounded px-3 py-1.5 hover:bg-destructive/10 transition-colors"
            >
              <Trash2 className="h-3.5 w-3.5" /> Clear all data for this project
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function wrapText(text: string, maxChars: number): string[] {
  const words = text.split(" ");
  const lines: string[] = [];
  let current = "";
  for (const word of words) {
    if ((current + " " + word).trim().length > maxChars && current) { lines.push(current.trim()); current = word; }
    else current = (current + " " + word).trim();
  }
  if (current) lines.push(current.trim());
  return lines.slice(0, 3);
}
